import { DetailsPageIsland } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIsland';

import { ReactComponent as EnquiryIcon } from '@rocco/icons/svg/message.svg';
import { BookmarkIcon } from 'lucide-react';
import { useRootData } from '~/modules/root';
import { Button } from '@rocco/components/button';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { Link } from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { cn } from '@rocco/utils/cn';

interface ProductIslandProps {
    product: ProductPageLoaderData['product'];
    openEnquiryModal: (changeUrl?: boolean, customHeading?: string) => void;
    primaryCTA: ProductPageLoaderData['primaryCTA'];
}

export const ProductIsland = ({
    product,
    openEnquiryModal,
}: ProductIslandProps) => {
    const { Title: productTitle, ID: productId, Professional } = product;

    const {
        Title: professionalTitle,
        Link: professionalLink,
        CompanyLogo,
        LogoBackgroundColor,
    } = Professional;

    const { appDisplayMode } = useRootData();

    const tracker = useTracker();

    const onEnquiryClick = (label: string) => {
        trackEnquireReveal(tracker, {
            professionalName: Professional.Title,
            itemID: productId,
            itemType: 'product',
            source: 'ProductIsland',
            ctaLabel: label,
        });

        openEnquiryModal(false, label);
    };

    const description = (
        <Link
            className="text-3 md:text-sm tracking-[0.0175rem] text-gray-500 text-ellipsis overflow-hidden"
            to={professionalLink}
        >
            <span className="hover:underline">{professionalTitle}</span>
        </Link>
    );

    return (
        <DetailsPageIsland
            key={`product-island-${productId}`}
            scrollThreshold={0}
            image={{
                image: {
                    src: CompanyLogo,
                    alt: professionalTitle,
                },
                background: LogoBackgroundColor,
                link: professionalLink,
                size: 'professionalLogo',
            }}
            displayMode={appDisplayMode}
            title={productTitle}
            description={description}
            primaryAction={
                <ProductIslandEnquiryButton
                    label={'Request Price / Quote'}
                    onClick={() => onEnquiryClick('Request Price / Quote')}
                    displayMode={appDisplayMode}
                    icon={
                        <EnquiryIcon
                            aria-label="Request Price / Quote"
                            className="w-4 fill-black"
                        />
                    }
                    type="primary"
                />
            }
            secondaryAction={
                <ProductIslandAddToDesignBoardButton
                    label="Shortlist Product"
                    displayMode={appDisplayMode}
                    productId={productId}
                />
            }
        />
    );
};

const ProductIslandEnquiryButton = ({
    label,
    onClick,
    displayMode,
    icon,
    type,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
    icon: React.ReactNode;
    type: 'primary' | 'secondary';
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color={type === 'primary' ? 'primary' : 'secondary70'}
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                {icon}
            </span>
        </Button>
    );
};

const ProductIslandAddToDesignBoardButton = ({
    label,
    displayMode,
    productId,
}: {
    label: string;
    displayMode: DisplayMode;
    productId: number;
}) => {
    const compact = displayMode === DisplayMode.Mobile;
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const saved = isInDesignBoard(productId, 'ProductPin');
    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="secondary70"
            onClick={() => handleFollow({ id: productId }, 'ProductPin')}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                <BookmarkIcon
                    aria-label={label}
                    className={cn(
                        'w-4',
                        saved ? 'fill-gray-900' : 'stroke-gray-900'
                    )}
                />
            </span>
        </Button>
    );
};
